import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { emptyLine, linesTotalCents, parseLines, type LineDraft } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { CustomerPicker } from '../ui/CustomerPicker';
import { Field, field } from '../create/Field';
import { LineItemBuilder } from './LineItemBuilder';
import { money } from '../money';
import { TaxRateField, taxBps } from './TaxRateField';

/**
 * An invoice with no job behind it.
 *
 * Most invoices come from work — bill the job, and the link carries the costs, the visits
 * and the history. This is for the ones that don't: a restocking fee, a diagnostic the
 * customer declined to proceed on, a materials-only sale over the counter. Small, real,
 * and previously impossible without inventing a fake job to hang it on.
 */
export function NewInvoiceSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [who, setWho] = useState({ customerId: '', propertyId: '' });
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [termsDays, setTermsDays] = useState('30');
  const [taxPct, setTaxPct] = useState('');

  // Whether this customer is exempt changes what the tax field should even SAY. The server
  // enforces it either way, but a rate box on an exempt customer's invoice invites somebody
  // to type a number that will be thrown away — a control that can only mislead.
  // See NewQuoteSheet: by id, not by scanning every customer in the shop.
  const chosen = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: who.customerId }),
    enabled: Boolean(who.customerId),
  });
  const exempt = chosen.data?.customer.taxExempt ?? false;
  const [memo, setMemo] = useState('');

  const create = useMutation(
    trpc.invoice.create.mutationOptions({
      onSuccess: (invoice) => {
        void qc.invalidateQueries();
        setWho({ customerId: '', propertyId: '' });
        setLines([emptyLine()]);
        setMemo('');
        onClose();
        void navigate({ to: '/invoices/$invoiceId', params: { invoiceId: invoice.id } });
      },
    }),
  );

  const usable = parseLines(lines);
  const subtotal = linesTotalCents(lines);
  const valid = who.customerId && usable.length > 0;

  return (
    <Sheet
      // Sonar-format width: a line-item builder is cramped at 420px (104).
      wide
      open={open}
      onClose={onClose}
      title="New invoice"
      footer={
        <Button
          className="w-full"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate({
              customerId: who.customerId,
              // Optional — plenty of one-off invoices aren't about an address at all.
              propertyId: who.propertyId || undefined,
              lines: usable,
              // undefined (not 0) when the box is blank — the server then applies the
              // shop's rate. See TaxRateField.taxBps.
              taxRateBps: taxBps(taxPct),
              termsDays: Number(termsDays) || 0,
              memo: memo.trim() || undefined,
              ...idem(),
            })
          }
        >
          {create.isPending ? 'Creating…' : `Create draft — ${money(subtotal)}`}
        </Button>
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}

        <p className="text-sm text-muted-foreground">
          For something you're billing that wasn't a job — a restocking fee, a call-out
          the customer declined, parts over the counter. If there was work, invoice the
          job instead so the costs follow it.
        </p>

        <CustomerPicker
          customerId={who.customerId}
          propertyId={who.propertyId}
          onChange={setWho}
        />

        <LineItemBuilder lines={lines} onChange={setLines} />

        <TaxRateField value={taxPct} onChange={setTaxPct} exempt={exempt} />

        <Field label="Due in (days)">
          <input
            className={field}
            value={termsDays}
            onChange={(e) => setTermsDays(e.target.value)}
            inputMode="numeric"
          />
        </Field>

        <Field label="Note on the invoice (the customer sees this)">
          <textarea
            className="min-h-[60px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Restocking fee for the returned condenser, per our call."
          />
        </Field>
      </div>
    </Sheet>
  );
}
